// src/pages/XOAuthCallbackPage.tsx
// X OAuth2 認証コールバックページ（orifoli用）
import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { authService } from '../services/authService';
import logoImage from '../assets/images/UI_orifoli_logo_black.png';
import logoImageFigryne from '../assets/images/UI_powered_by_figryne.png';

const XOAuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [status, setStatus] = useState<'processing' | 'success' | 'error'>('processing');
  const [message, setMessage] = useState('X認証を処理中...');
  const [fadeOut, setFadeOut] = useState(false);
  const isProcessingRef = useRef(false);

  // フェードアウト後にナビゲート
  const navigateWithFade = (path: string, delay: number = 1000, state?: Record<string, unknown>) => {
    setTimeout(() => {
      setFadeOut(true);
      setTimeout(() => {
        navigate(path, state ? { state } : undefined);
      }, 400); // フェードアウトアニメーション時間
    }, delay);
  };

  useEffect(() => {
    const handleCallback = async () => {
      // 認証状態のロード中は待機
      if (authLoading) {
        return;
      }

      // 二重実行を防ぐ（React 18 Strict Mode対策 + 再マウント対策）
      if (isProcessingRef.current) {
        return;
      }

      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');

      // コードがない場合、すでに処理済みか確認
      if (!code || !state) {
        // 処理中フラグがある場合は何もしない（成功処理中）
        if (sessionStorage.getItem('xAuthProcessing')) {
          return;
        }
        // エラーパラメータがある場合のみエラー表示
        if (error) {
          setStatus('error');
          setMessage('認証がキャンセルされました');
          navigateWithFade('/', 1500);
        }
        // パラメータがない場合は単にホームへ
        return;
      }

      // ログインモードかどうかを判定
      const isLoginMode = state.startsWith('login:');

      isProcessingRef.current = true;
      sessionStorage.setItem('xAuthProcessing', 'true');

      if (error) {
        sessionStorage.removeItem('xAuthProcessing');
        setStatus('error');
        setMessage('認証がキャンセルされました');
        navigateWithFade('/', 1500);
        return;
      }

      try {
        const useLocalhost = window.location.hostname === 'localhost';
        const isProduction = import.meta.env.MODE === 'production';

        if (isLoginMode) {
          // ログインモード: 認証なしでexchangeXCodeを呼ぶ
          const functionsUrl = isProduction
            ? 'https://asia-northeast1-figryne-prod.cloudfunctions.net/exchangeXCode'
            : 'https://asia-northeast1-aerobic-pivot-440910-a3.cloudfunctions.net/exchangeXCode';

          const response = await fetch(functionsUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ code, state, useLocalhost, app: 'orifoli' })
          });

          const data = await response.json();

          if (response.ok && data.success && data.customToken) {
            // カスタムトークンでサインイン
            await authService.signInWithCustomToken(data.customToken);

            sessionStorage.removeItem('xAuthProcessing');
            setStatus('success');
            setMessage(`@${data.xUsername} でログインしました`);

            // localStorageからリダイレクト先を取得
            const savedRedirectPath = localStorage.getItem('xAuthRedirectPath');
            localStorage.removeItem('xAuthRedirectPath');

            // 新規ユーザーならプロフィール設定へ、既存ユーザーは保存されたパスまたはホームへ
            if (data.isNewUser) {
              navigateWithFade('/settings', 1000);
            } else if (savedRedirectPath) {
              navigateWithFade(savedRedirectPath, 1000);
            } else {
              navigateWithFade('/', 1000);
            }
          } else {
            sessionStorage.removeItem('xAuthProcessing');
            setStatus('error');
            setMessage(data.message || data.error || 'ログインに失敗しました');
            navigateWithFade('/', 2000);
          }
        } else {
          // 連携モード: 認証トークン付きでexchangeXCodeを呼ぶ
          if (!user) {
            // ポップアップでは認証状態が復元されていない可能性がある
            // BroadcastChannel または window.opener で親ウィンドウに処理を委任
            setMessage('認証処理中...');

            let delegated = false;

            // 1. BroadcastChannel で親ウィンドウに通知（COOP対策）
            try {
              const channel = new BroadcastChannel('x_oauth_channel');
              channel.postMessage({ type: 'X_AUTH_CALLBACK', code, state });
              delegated = true;
              channel.close();
            } catch (e) {
              console.warn('XOAuthCallback: BroadcastChannel failed', e);
            }

            // 2. フォールバック: window.opener で通知
            if (!delegated && window.opener) {
              try {
                window.opener.postMessage({ type: 'X_AUTH_CALLBACK', code, state }, '*');
                delegated = true;
              } catch (e) {
                console.error('XOAuthCallback: postMessage failed', e);
              }
            }

            if (delegated) {
              setTimeout(() => {
                window.close();
              }, 1000);
              return;
            }

            // どちらも使えない場合はエラー
            setStatus('error');
            setMessage('ログインが必要です');
            navigateWithFade('/', 1500);
            return;
          }

          const idToken = await user.getIdToken();
          const functionsUrl = isProduction
            ? 'https://asia-northeast1-figryne-prod.cloudfunctions.net/exchangeXCode'
            : 'https://asia-northeast1-aerobic-pivot-440910-a3.cloudfunctions.net/exchangeXCode';

          const response = await fetch(functionsUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${idToken}`
            },
            body: JSON.stringify({ code, state, useLocalhost, app: 'orifoli' })
          });

          const data = await response.json();

          if (response.ok && data.success) {
            sessionStorage.removeItem('xAuthProcessing');
            setStatus('success');
            setMessage(`@${data.xUsername} の認証が完了しました`);

            // ポップアップまたは新しいタブで開かれた場合、親ウィンドウに通知して閉じる
            let notifiedParent = false;

            // BroadcastChannel で通知（COOP対策）
            try {
              const channel = new BroadcastChannel('x_oauth_channel');
              channel.postMessage({ type: 'X_AUTH_SUCCESS' });
              channel.close();
              notifiedParent = true;
            } catch (e) {
              // BroadcastChannel未対応の場合は無視
            }

            // フォールバック: window.opener
            if (window.opener) {
              try {
                window.opener.postMessage({ type: 'X_AUTH_SUCCESS' }, '*');
                notifiedParent = true;
              } catch (e) {
                // postMessageが失敗した場合は無視
              }
            }

            if (notifiedParent) {
              setTimeout(() => {
                window.close();
              }, 1500);
              return;
            }

            // 保存されたパスに戻る、なければホームへ
            const returnPath = localStorage.getItem('xAuthReturnPath') || '/';
            localStorage.removeItem('xAuthReturnPath');
            navigateWithFade(returnPath, 1000);
          } else {
            setStatus('error');
            setMessage(data.message || data.error || '認証に失敗しました');
            navigateWithFade('/', 2000);
          }
        }
      } catch (err) {
        console.error('OAuth callback error:', err);
        setStatus('error');
        setMessage('認証処理中にエラーが発生しました');
        navigateWithFade('/', 2000);
      }
    };

    handleCallback();
  }, [searchParams, user, navigate, authLoading]);

  return (
    <div className={`fixed inset-0 z-50 min-h-screen bg-[#f8f7f8] flex items-center justify-center transition-opacity duration-300 ${fadeOut ? 'opacity-0' : 'opacity-100'}`}>
      <div className={`text-center transition-all duration-300 ${fadeOut ? 'opacity-0 scale-95' : 'opacity-100 scale-100'}`}>
        <div className="mb-6">
          <img
            src={logoImage}
            alt="ORIFOLI"
            className="h-16 mx-auto"
            onContextMenu={(e) => e.preventDefault()}
            draggable={false}
          />
          <img
            src={logoImageFigryne}
            alt="Powerd by Figryne"
            className="h-5 mx-auto mt-8"
            onContextMenu={(e) => e.preventDefault()}
            draggable={false}
          />
        </div>

        {status === 'processing' && (
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-black mx-auto mb-4"></div>
        )}

        {status === 'success' && (
          <div className="mb-4">
            <svg className="w-12 h-12 text-green-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
        )}

        {status === 'error' && (
          <div className="mb-4">
            <svg className="w-12 h-12 text-red-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        )}

        <p className={`text-sm ${
          status === 'success' ? 'text-green-300' :
          status === 'error' ? 'text-red-300' :
          'text-white/80'
        }`}>
          {message}
        </p>
      </div>
    </div>
  );
};

export default XOAuthCallbackPage;
